import { IconButton } from "@dropbox/dig-components/buttons";
import type { ControlRefObject } from "@dropbox/dig-components/tooltips";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Dismiss_AppsSyncingNavIconTooltip } from "@mirage/analytics/events/types/dismiss_apps_syncing_nav_icon_tooltip";
import { PAP_Shown_AppsSyncingNavIconTooltip } from "@mirage/analytics/events/types/shown_apps_syncing_nav_icon_tooltip";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import { useEffect, useRef } from "react";
import styles from "./GlobalNavAppsIconTooltip.module.css";
import { useAppsTooltip } from "./useAppsTooltip";

type Props = {
  triggerRef: React.RefObject<HTMLElement | Element>;
};

export function GlobalNavAppsIconTooltip({ triggerRef }: Props) {
  const { isTooltipOpen, setDismissed: setTooltipDismissed } = useAppsTooltip();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const exposureLoggedRef = useRef(false);
  const tooltipRef = useRef<ControlRefObject>(null);

  useEffect(() => {
    if (exposureLoggedRef.current || !isTooltipOpen) return;
    exposureLoggedRef.current = true;
    reportPapEvent(
      PAP_Shown_AppsSyncingNavIconTooltip({
        featureLine: "onboarding",
        isAddAppsTooltip: true,
      }),
    );
  }, [isTooltipOpen, reportPapEvent]);

  useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      if (!tooltipRef.current) return;
      // Trigger repositioning logic here
      tooltipRef.current?.update?.();
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [triggerRef]);

  const handleDismiss = () => {
    reportPapEvent(
      PAP_Dismiss_AppsSyncingNavIconTooltip({
        featureLine: "onboarding",
        isAddAppsTooltip: true,
      }),
    );
    setTooltipDismissed(true);
  };

  return (
    <DigTooltip.Control
      variant="rich"
      open={isTooltipOpen}
      triggerRef={triggerRef}
      ref={tooltipRef}
      auto
      placement="right"
      maxWidth={300}
      positioningStrategy="fixed"
    >
      <IconButton
        variant="borderless"
        onClick={handleDismiss}
        className={styles.dismissButton}
        aria-label={i18n.t("close")}
      >
        <UIIcon src={CloseLine} />
      </IconButton>
      <Box className={styles.tourTooltip}>
        <Title size="small" weightVariant="emphasized">
          {i18n.t("add_apps_tooltip_title")}
        </Title>
        <Text tagName="p">{i18n.t("add_apps_tooltip_subtitle")}</Text>
      </Box>
    </DigTooltip.Control>
  );
}
